/**
 * useChatbot Hook
 *
 * Main hook for AI Chatbot functionality.
 * Combines state management with WebSocket communication.
 * Phase 7: Frontend Integration
 *
 * @module UseChatbot
 */

'use client';

import { useReducer, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocale } from 'next-intl';
import { logger } from '@axon/ui/utils';

import { useRouter, usePathname } from '@/i18n/navigation';

import type {
  ChatbotState,
  ChatbotAction,
  ChatMessage,
  ChatLanguage,
  FeedbackRating,
  StreamCompleteData,
  MessageEnrichedData,
  MessageMetadata,
  NavigationData,
  ProactiveSuggestion,
} from '../types/chatbot.types';
import { useFormPrefill } from '../providers/FormPrefillProvider';
import {
  loadFromStorage,
  saveToStorage,
  CHATBOT_STORAGE_KEYS,
} from '../utils/persisted-state.utils';
import { SPOTLIGHT_DELAYS } from '../constants/spotlight.constants';

import { useChatSocket } from './useChatSocket';
import { useSpotlight } from './useSpotlight';

// ============================================================================
// Constants
// ============================================================================

/** Local storage key for persisting chat state (messages) */
const STORAGE_KEY = 'axon-chatbot-state';

/** Local storage key for navigation window behavior preference (Phase 19) */
const STORAGE_KEY_NAV_BEHAVIOR = 'chatbot_nav_behavior';

/** Initial state for the chatbot */
const initialState: ChatbotState = {
  isOpen: false,
  isMinimized: false,
  session: null,
  messages: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  proactiveSuggestions: [],
};

/** Type guard for UI state validation */
function isValidUIState(value: unknown): value is { isOpen: boolean; isMinimized: boolean } {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return typeof obj['isOpen'] === 'boolean' && typeof obj['isMinimized'] === 'boolean';
}

// ============================================================================
// Reducer
// ============================================================================

/**
 * Chatbot state reducer
 */
function chatbotReducer(state: ChatbotState, action: ChatbotAction): ChatbotState {
  switch (action.type) {
    case 'OPEN_CHAT':
      return { ...state, isOpen: true, isMinimized: false, unreadCount: 0 };

    case 'CLOSE_CHAT':
      return { ...state, isOpen: false };

    case 'TOGGLE_CHAT':
      return state.isMinimized
        ? { ...state, isMinimized: false, unreadCount: 0 }
        : { ...state, isOpen: !state.isOpen, isMinimized: false, unreadCount: 0 };

    case 'MINIMIZE_CHAT':
      return { ...state, isMinimized: true };

    case 'SET_SESSION':
      return { ...state, session: action.payload };

    case 'CLEAR_SESSION':
      // Drop only the CONVERSATION, not the live socket session. Nulling the
      // whole session made the status badge read "connecting" forever after a
      // "Clear" (the badge derives from session.isConnected) even though the
      // socket was still connected, and it blocked sendMessage (which requires
      // a session). Resetting conversationId to '' mirrors the closed-recovery
      // path: the next message starts a fresh conversation and onStreamComplete
      // back-fills the new conversationId.
      return {
        ...state,
        session: state.session ? { ...state.session, conversationId: '' } : null,
      };

    case 'ADD_MESSAGE':
      return {
        ...state,
        messages: [...state.messages, action.payload],
        unreadCount:
          action.payload.role === 'assistant' && (!state.isOpen || state.isMinimized)
            ? state.unreadCount + 1
            : state.unreadCount,
      };

    case 'UPDATE_MESSAGE':
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.payload.id
            ? {
                ...m,
                ...action.payload.updates,
                // Merge metadata instead of replacing it. Partial updates (e.g.
                // adding feedback) previously wiped sibling keys like
                // followUpSuggestions/sources, making the suggestion chips
                // vanish after the user rated an answer. Callers that pre-merge
                // (onMessageEnriched/onStreamComplete) are unaffected — a shallow
                // re-merge of the same object is idempotent.
                metadata: action.payload.updates.metadata
                  ? { ...m.metadata, ...action.payload.updates.metadata }
                  : m.metadata,
              }
            : m
        ),
      };

    case 'APPEND_CHUNK': {
      const lastMessage = state.messages[state.messages.length - 1];
      if (lastMessage?.isStreaming) {
        return {
          ...state,
          messages: [
            ...state.messages.slice(0, -1),
            { ...lastMessage, content: lastMessage.content + action.payload.chunk },
          ],
        };
      }
      return state;
    }

    case 'SET_TYPING':
      return {
        ...state,
        session: state.session ? { ...state.session, isTyping: action.payload } : null,
      };

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };

    case 'CLEAR_MESSAGES':
      return { ...state, messages: [], unreadCount: 0 };

    case 'MARK_MESSAGES_READ':
      return { ...state, unreadCount: 0 };

    case 'ADD_PROACTIVE_SUGGESTION':
      return {
        ...state,
        proactiveSuggestions: [...state.proactiveSuggestions, action.payload],
      };

    case 'DISMISS_PROACTIVE_SUGGESTION':
      return {
        ...state,
        proactiveSuggestions: state.proactiveSuggestions.filter((s) => s.id !== action.payload),
      };

    default:
      return state;
  }
}

// ============================================================================
// Hook Options
// ============================================================================

export interface UseChatbotOptions {
  /** Auto-connect when chat opens */
  autoConnect?: boolean;
  /** Persist messages to local storage */
  persistMessages?: boolean;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Main hook for AI Chatbot functionality
 *
 * @example
 * const {
 *   isOpen,
 *   messages,
 *   session,
 *   openChat,
 *   closeChat,
 *   sendMessage,
 * } = useChatbot();
 */
export function useChatbot(options: UseChatbotOptions = {}) {
  const { autoConnect = true, persistMessages = false } = options;
  const locale = useLocale() as ChatLanguage;
  const router = useRouter();
  const pathname = usePathname();
  const { setPrefillData } = useFormPrefill();
  const spotlight = useSpotlight({
    onNavigate: (path) => router.push(path),
    getCurrentPath: () =>
      typeof window !== 'undefined'
        ? window.location.pathname.replace(/^\/(?:en|tr)/, '')
        : pathname,
  });

  // Initialize with persisted UI state
  const [state, dispatch] = useReducer(chatbotReducer, initialState, (initial) => {
    const persistedUIState = loadFromStorage(
      CHATBOT_STORAGE_KEYS.UI_STATE,
      { isOpen: false, isMinimized: false },
      isValidUIState
    );
    return {
      ...initial,
      isOpen: persistedUIState.isOpen,
      isMinimized: persistedUIState.isMinimized,
    };
  });

  // Track the last user message for retry after closed-conversation recovery
  const pendingRetryMessageRef = useRef<string | null>(null);

  // WebSocket connection
  const {
    isConnected,
    isConnecting,
    connect,
    disconnect,
    sendMessage: socketSendMessage,
    confirmAction: socketConfirmAction,
    sendFeedback: socketSendFeedback,
    sendScreenContext: socketSendScreenContext,
    clearHistory: socketClearHistory,
    leaveConversation: socketLeaveConversation,
  } = useChatSocket({
    onConnect: (data) => {
      logger.info('[useChatbot] Chat session connected', {
        conversationId: data.conversationId,
      });
      dispatch({
        type: 'SET_SESSION',
        payload: {
          conversationId: data.conversationId,
          isConnected: true,
          isTyping: false,
          language: data.language,
        },
      });
      // Clear any stale errors from previous failed connections
      dispatch({ type: 'SET_ERROR', payload: null });
    },
    onMessage: (message) => {
      logger.debug('[useChatbot] Received message');
      dispatch({ type: 'ADD_MESSAGE', payload: message });
      dispatch({ type: 'SET_LOADING', payload: false });
      // A full message means the bot is no longer typing. Don't rely solely on
      // a separate chat:typing(false) event — if it's dropped or raced (e.g.
      // under heavy backend load) the indicator hangs forever.
      dispatch({ type: 'SET_TYPING', payload: false });
    },
    onChunk: (chunk) => {
      dispatch({ type: 'APPEND_CHUNK', payload: { chunk } });
    },
    onTyping: (isTyping) => {
      dispatch({ type: 'SET_TYPING', payload: isTyping });
    },
    onError: (error) => {
      logger.error('[useChatbot] Chat error', {
        code: error?.code,
        message: error?.message,
      });

      // The turn ended (in failure) — the bot is no longer typing.
      dispatch({ type: 'SET_TYPING', payload: false });

      // Detect "closed conversation" error and recover automatically.
      // The backend throws this when the session's conversationId points to a
      // conversation that was closed by the stale-conversation cron or archived
      // via clear-history. Recovery: leave the old conversation on the backend
      // (resets session.conversationId to null), clear the frontend session so
      // the next message creates a fresh conversation, and retry the pending message.
      const isClosedConversation =
        error?.message?.toLowerCase().includes('closed conversation') ||
        error?.code === 'CONVERSATION_CLOSED';

      if (isClosedConversation && state.session?.conversationId) {
        logger.info('[useChatbot] Recovering from closed conversation', {
          staleConversationId: state.session.conversationId,
        });

        // Tell the backend to clear the stale conversationId from the socket session
        socketLeaveConversation(state.session.conversationId);

        // Clear the frontend session's conversationId so a new conversation is created
        dispatch({
          type: 'SET_SESSION',
          payload: { ...state.session, conversationId: '' },
        });

        // Remove the failed streaming placeholder message
        const lastMsg = state.messages[state.messages.length - 1];
        if (lastMsg?.isStreaming) {
          dispatch({
            type: 'UPDATE_MESSAGE',
            payload: {
              id: lastMsg.id,
              updates: { isStreaming: false, content: '', type: 'text' },
            },
          });
        }

        dispatch({ type: 'SET_LOADING', payload: false });

        // Retry the pending message if one was tracked
        const retryContent = pendingRetryMessageRef.current;
        if (retryContent) {
          pendingRetryMessageRef.current = null;
          // Use a short delay to let the state update propagate before retry
          setTimeout(() => {
            socketSendMessage(retryContent).catch((retryErr) => {
              logger.error('[useChatbot] Retry after closed-conversation recovery failed', {
                error: String(retryErr),
              });
              dispatch({
                type: 'SET_ERROR',
                payload: 'Failed to send message. Please try again.',
              });
            });
          }, 100);
        }
        return;
      }

      dispatch({ type: 'SET_ERROR', payload: error.message });
    },
    onStreamComplete: (data: StreamCompleteData) => {
      // Clear pending retry — message was processed successfully
      pendingRetryMessageRef.current = null;

      // Update session conversationId if it was null (first message creates the conversation on backend)
      if (data.conversationId && state.session && !state.session.conversationId) {
        dispatch({
          type: 'SET_SESSION',
          payload: { ...state.session, conversationId: data.conversationId },
        });
      }

      // Mark last streaming message as complete and update type/metadata
      const lastMessage = state.messages[state.messages.length - 1];
      if (lastMessage?.isStreaming) {
        const updates: Partial<ChatMessage> = { isStreaming: false };
        // Update message type if provided (e.g., 'data_query')
        if (data.type) {
          updates.type = data.type as ChatMessage['type'];
        }
        // Merge metadata (originalQuery, sources, etc.) if provided.
        // serverMessageId anchors post-hoc `chat:message_enriched` merges.
        if ((data.metadata && typeof data.metadata === 'object') || data.messageId) {
          updates.metadata = {
            ...lastMessage.metadata,
            ...(data.metadata ?? {}),
            ...(data.messageId ? { serverMessageId: data.messageId } : {}),
          } as MessageMetadata;
        }
        dispatch({
          type: 'UPDATE_MESSAGE',
          payload: { id: lastMessage.id, updates },
        });

        // Phase 19E: Trigger spotlight if metadata contains spotlight data
        const spotlightData = (data.metadata as MessageMetadata | undefined)?.spotlight;
        if (spotlightData?.steps && spotlightData.steps.length > 0) {
          // Auto-minimize chat so it doesn't obstruct highlighted elements
          dispatch({ type: 'MINIMIZE_CHAT' });

          // Auto-expand sidebar if tour targets it (collapsed sidebar can't show nav items)
          const needsSidebar = spotlightData.steps.some((s) => s.spotlightId === 'main-sidebar');
          if (needsSidebar && typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('spotlight:expand-sidebar'));
            // Wait for sidebar expand animation before measuring elements
            setTimeout(() => {
              spotlight.startSpotlight(spotlightData.steps);
            }, SPOTLIGHT_DELAYS.SIDEBAR_EXPAND_WAIT);
          } else {
            spotlight.startSpotlight(spotlightData.steps);
          }
        }
      }
      dispatch({ type: 'SET_LOADING', payload: false });
      // Stream finished — clear the typing indicator even if the dedicated
      // chat:typing(false) event never arrived (dropped/raced under load).
      dispatch({ type: 'SET_TYPING', payload: false });
    },
    onDisconnect: () => {
      if (state.session) {
        dispatch({
          type: 'SET_SESSION',
          payload: { ...state.session, isConnected: false },
        });
      }
    },
    // Post-hoc enrichment: the message shipped immediately with static
    // fallbacks; merge the LLM follow-ups/insight in when they land.
    onMessageEnriched: (data: MessageEnrichedData) => {
      const target =
        state.messages.find((m) => m.metadata?.serverMessageId === data.messageId) ??
        // Fallback: the enriched message is virtually always the most
        // recent assistant message (enrichment lands seconds after it).
        [...state.messages].reverse().find((m) => m.role === 'assistant' && !m.isStreaming);
      if (!target) return;
      dispatch({
        type: 'UPDATE_MESSAGE',
        payload: {
          id: target.id,
          updates: {
            metadata: { ...target.metadata, ...data.metadata } as MessageMetadata,
          },
        },
      });
    },
    // Phase 18: Action confirmation request - add as assistant message with confirmation metadata
    onActionConfirm: (data) => {
      logger.debug('[useChatbot] Action confirmation request', { actionType: data.actionType });
      const confirmMessage: ChatMessage = {
        id: `action-confirm-${Date.now()}`,
        conversationId: data.conversationId || state.session?.conversationId || '',
        role: 'assistant',
        content:
          data.description || `Confirm ${data.actionType} on ${data.entityType} ${data.entityId}?`,
        type: 'action_confirmation',
        metadata: {
          actionConfirmation: {
            confirmationId: data.confirmationId,
            actionType: data.actionType,
            entityType: data.entityType,
            entityId: data.entityId,
            safetyLevel: data.safetyLevel,
            requiresReason: data.requiresReason,
          },
        },
        createdAt: new Date(),
      };
      dispatch({ type: 'ADD_MESSAGE', payload: confirmMessage });
      dispatch({ type: 'SET_LOADING', payload: false });
    },
    // Phase 18: Action execution result - add as assistant message with result metadata
    onActionResult: (data) => {
      logger.debug('[useChatbot] Action result', { success: data.success });
      const resultMessage: ChatMessage = {
        id: `action-result-${Date.now()}`,
        conversationId: data.conversationId || state.session?.conversationId || '',
        role: 'assistant',
        content:
          data.message || (data.success ? 'Action completed successfully.' : 'Action failed.'),
        type: 'action_result',
        metadata: {
          actionResult: {
            success: data.success,
            actionType: data.actionType,
            entityType: data.entityType,
            entityId: data.entityId,
            changes: data.changes,
          },
        },
        createdAt: new Date(),
      };
      dispatch({ type: 'ADD_MESSAGE', payload: resultMessage });
      dispatch({ type: 'SET_LOADING', payload: false });
    },
    // Phase 18: Proactive suggestion
    onProactiveSuggestion: (suggestion: ProactiveSuggestion) => {
      logger.debug('[useChatbot] Proactive suggestion', { type: suggestion.type });
      dispatch({ type: 'ADD_PROACTIVE_SUGGESTION', payload: suggestion });
    },
    // Phase 19: Navigation command
    onNavigate: (data: NavigationData) => {
      logger.info('[useChatbot] Navigation command', { path: data.path, label: data.label });
      if (data.path) {
        // Phase 19D: Store form pre-fill data before navigation
        if (data.formPrefill && Object.keys(data.formPrefill).length > 0) {
          setPrefillData(data.formPrefill);
        }

        router.push(data.path);

        // Apply navigation window behavior preference
        const navBehavior =
          typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY_NAV_BEHAVIOR) : null;
        if (navBehavior === 'minimize') {
          dispatch({ type: 'MINIMIZE_CHAT' });
        }
        // If no preference set yet, show prompt as a chat message
        if (!navBehavior && typeof window !== 'undefined') {
          const navLabel = data.label ?? data.path;
          const preferenceMessage: ChatMessage = {
            id: `nav-pref-${Date.now()}`,
            conversationId: state.session?.conversationId ?? '',
            role: 'assistant',
            content:
              locale === 'tr'
                ? `${navLabel} sayfasına yönlendiriliyor. Sohbet penceresini ne yapayım?`
                : `Navigating to ${navLabel}. What should I do with the chat window?`,
            type: 'text',
            metadata: {
              clarificationOptions:
                locale === 'tr' ? ['Sohbeti küçült', 'Açık tut'] : ['Minimize chat', 'Keep open'],
            },
            createdAt: new Date(),
          };
          dispatch({ type: 'ADD_MESSAGE', payload: preferenceMessage });
        }
      }
    },
  });

  // Connect when chat opens (if autoConnect enabled)
  useEffect(() => {
    if (autoConnect && state.isOpen && !isConnected && !isConnecting) {
      connect(locale);
    }
  }, [autoConnect, state.isOpen, isConnected, isConnecting, connect, locale]);

  // Persist UI state (isOpen, isMinimized) to localStorage
  useEffect(() => {
    saveToStorage(CHATBOT_STORAGE_KEYS.UI_STATE, {
      isOpen: state.isOpen,
      isMinimized: state.isMinimized,
    });
  }, [state.isOpen, state.isMinimized]);

  // Phase 19C: Emit screen context when pathname changes and socket is connected
  useEffect(() => {
    if (isConnected && pathname) {
      socketSendScreenContext(pathname);
    }
  }, [isConnected, pathname, socketSendScreenContext]);

  // Persist messages to local storage
  useEffect(() => {
    if (persistMessages && state.messages.length > 0) {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            messages: state.messages.slice(-50), // Keep last 50 messages
          })
        );
      } catch (error) {
        logger.warn('[useChatbot] Failed to persist messages', { error: String(error) });
      }
    }
  }, [persistMessages, state.messages]);

  // Restore messages from local storage on mount
  useEffect(() => {
    if (persistMessages) {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          const messages = parsed?.messages;
          if (Array.isArray(messages)) {
            messages.forEach((message: ChatMessage) => {
              dispatch({ type: 'ADD_MESSAGE', payload: message });
            });
          }
        }
      } catch (error) {
        logger.warn('[useChatbot] Failed to restore messages, clearing stale data', {
          error: String(error),
        });
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, [persistMessages]);

  // ============================================================================
  // Actions
  // ============================================================================

  const openChat = useCallback(() => {
    dispatch({ type: 'OPEN_CHAT' });
  }, []);

  const closeChat = useCallback(() => {
    if (state.session?.conversationId) {
      socketLeaveConversation(state.session.conversationId);
    }
    disconnect();
    dispatch({ type: 'CLOSE_CHAT' });
  }, [state.session?.conversationId, socketLeaveConversation, disconnect]);

  const toggleChat = useCallback(() => {
    dispatch({ type: 'TOGGLE_CHAT' });
  }, []);

  const minimizeChat = useCallback(() => {
    dispatch({ type: 'MINIMIZE_CHAT' });
  }, []);

  const reconnect = useCallback(async () => {
    dispatch({ type: 'SET_ERROR', payload: null });
    disconnect();
    connect(locale);
  }, [connect, disconnect, locale]);

  const sendMessage = useCallback(
    async (content: string) => {
      const trimmedContent = content.trim();
      if (!trimmedContent || !state.session) {
        logger.warn('[useChatbot] Cannot send: Empty content or no session');
        return;
      }

      // Phase 19: Intercept navigation window behavior preference selection (EN + TR)
      const isMinimize = trimmedContent === 'Minimize chat' || trimmedContent === 'Sohbeti küçült';
      const isKeepOpen = trimmedContent === 'Keep open' || trimmedContent === 'Açık tut';
      if (isMinimize || isKeepOpen) {
        const preference = isMinimize ? 'minimize' : 'keep_open';
        if (typeof window !== 'undefined') {
          localStorage.setItem(STORAGE_KEY_NAV_BEHAVIOR, preference);
        }
        // Add confirmation message (bilingual)
        const confirmMessage: ChatMessage = {
          id: `nav-pref-confirm-${Date.now()}`,
          conversationId: state.session.conversationId,
          role: 'assistant',
          content:
            locale === 'tr'
              ? preference === 'minimize'
                ? 'Anlaşıldı! Sayfa değiştiğinde sohbet penceresini küçülteceğim.'
                : 'Anlaşıldı! Sayfa değiştiğinde sohbet penceresini açık tutacağım.'
              : preference === 'minimize'
                ? "Got it! I'll minimize the chat window when navigating."
                : "Got it! I'll keep the chat window open when navigating.",
          type: 'text',
          createdAt: new Date(),
        };
        dispatch({ type: 'ADD_MESSAGE', payload: confirmMessage });
        if (preference === 'minimize') {
          dispatch({ type: 'MINIMIZE_CHAT' });
        }
        return;
      }

      // Add user message immediately
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        conversationId: state.session.conversationId,
        role: 'user',
        content: trimmedContent,
        type: 'text',
        createdAt: new Date(),
      };
      dispatch({ type: 'ADD_MESSAGE', payload: userMessage });

      // Add placeholder for assistant response (streaming)
      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        conversationId: state.session.conversationId,
        role: 'assistant',
        content: '',
        type: 'text',
        createdAt: new Date(),
        isStreaming: true,
      };
      dispatch({ type: 'ADD_MESSAGE', payload: assistantMessage });
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      // Track last sent message for retry on closed-conversation recovery
      pendingRetryMessageRef.current = trimmedContent;

      try {
        await socketSendMessage(trimmedContent);
      } catch (error) {
        logger.error('[useChatbot] Failed to send message', { error: String(error) });
        dispatch({
          type: 'SET_ERROR',
          payload: error instanceof Error ? error.message : 'Failed to send message',
        });
        // Remove the streaming placeholder on error
        dispatch({
          type: 'UPDATE_MESSAGE',
          payload: {
            id: assistantMessage.id,
            updates: {
              isStreaming: false,
              content: 'Failed to get response. Please try again.',
              type: 'error',
            },
          },
        });
      }
    },
    [state.session, socketSendMessage, locale]
  );

  const sendFeedback = useCallback(
    (messageId: string, rating: FeedbackRating, comment?: string) => {
      socketSendFeedback(messageId, rating, comment);
      dispatch({
        type: 'UPDATE_MESSAGE',
        payload: {
          id: messageId,
          updates: {
            metadata: { feedback: { rating, comment } },
          },
        },
      });
    },
    [socketSendFeedback]
  );

  const confirmAction = useCallback(
    (confirmationId: string, confirmed: boolean, reason?: string) => {
      socketConfirmAction(confirmationId, confirmed, reason);
    },
    [socketConfirmAction]
  );

  const dismissSuggestion = useCallback((suggestionId: string) => {
    dispatch({ type: 'DISMISS_PROACTIVE_SUGGESTION', payload: suggestionId });
  }, []);

  const clearMessages = useCallback(() => {
    // Notify backend to archive conversation and clear user memories
    socketClearHistory();
    dispatch({ type: 'CLEAR_MESSAGES' });
    // Clear the session so the next message creates a fresh conversation.
    // The backend's handleClearHistory archives the current conversation but does
    // NOT reset session.conversationId — sending chat:leave ensures the backend
    // session is also cleared, preventing "Cannot add message to closed conversation".
    if (state.session?.conversationId) {
      socketLeaveConversation(state.session.conversationId);
      dispatch({ type: 'CLEAR_SESSION' });
    }
    if (persistMessages) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        logger.warn('[useChatbot] Failed to clear persisted messages', { error: String(error) });
      }
    }
  }, [persistMessages, socketClearHistory, socketLeaveConversation, state.session?.conversationId]);

  // Mark assistant messages as read when chat is visible
  useEffect(() => {
    if (state.isOpen && !state.isMinimized && state.unreadCount > 0) {
      dispatch({ type: 'MARK_MESSAGES_READ' });
    }
  }, [state.isOpen, state.isMinimized, state.unreadCount]);

  // ============================================================================
  // Return Value
  // ============================================================================

  return useMemo(
    () => ({
      // State
      isOpen: state.isOpen,
      isMinimized: state.isMinimized,
      session: state.session,
      messages: state.messages,
      unreadCount: state.unreadCount,
      isLoading: state.isLoading,
      error: state.error,
      proactiveSuggestions: state.proactiveSuggestions,

      // Connection state
      isConnected,
      isConnecting,

      // Actions
      openChat,
      closeChat,
      toggleChat,
      minimizeChat,
      sendMessage,
      sendFeedback,
      clearMessages,
      reconnect,
      confirmAction,
      dismissSuggestion,
      connect: () => connect(locale),
      disconnect,

      // Spotlight (Phase 19E)
      spotlight,
      // Guided tour controls exposed for non-chatbot callers (e.g. System Flow
      // guided-demo launcher). Reuses the same spotlight instance — no duplicate overlay.
      startGuidedTour: spotlight.startSpotlight,
      stopGuidedTour: spotlight.stopSpotlight,
    }),
    [
      state.isOpen,
      state.isMinimized,
      state.session,
      state.messages,
      state.unreadCount,
      state.isLoading,
      state.error,
      state.proactiveSuggestions,
      isConnected,
      isConnecting,
      openChat,
      closeChat,
      toggleChat,
      minimizeChat,
      sendMessage,
      sendFeedback,
      clearMessages,
      reconnect,
      confirmAction,
      dismissSuggestion,
      connect,
      disconnect,
      locale,
      spotlight,
    ]
  );
}
